import {
  ReadingsDataRawResponse,
  useChartData,
} from "@inrange/building-manager-api-client";
import { SiteAllowStringValues } from "@inrange/building-manager-api-client/models-site";
import { useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { DateRangePicker } from "../../../components";
import Loading from "../../Loading";
import ChartRaw from "./ChartRaw";
import ReadingsSeriesPicker from "./ReadingsSeriesPicker";
import RecentDataTableRaw from "./RecentDataTableRaw";
import SourceTypeCounts from "./SourceTypeCounts";
import { Formatters, OutlierMode, RawPresetName, RenderMode } from "./types";
import UpdatedReadingsRaw from "./UpdatedReadingsRaw";

// The "Production/Export" preset: production/export/feed-in meter + inverter
// energy + EPM sell series (hardwareType|readingName patterns).
const PRODUCTION_EXPORT_FILTER = new Set([
  "meter|METER_SITE_EXPORT_ENERGY_ACTIVE",
  "meter|METER_PV_EXPORT_ENERGY_ACTIVE",
  "meter|METER_BATTERY_EXPORT_ENERGY_ACTIVE",
  "meter|METER_BATTERY_IMPORT_ENERGY_ACTIVE",
  "epm|EPM_TOTAL_SELL",
  "inverter|INVERTER_E_TODAY",
  "meter|Production",
  "meter|FeedIn",
]);

interface AvailableReading {
  key: string;
  hardwareType: string;
  serialNumber: string;
  readingName: string;
}

// Helper to create a reading key from parts
const makeReadingKey = (
  hardwareType: string,
  serialNumber: string,
  readingName: string
) => `${hardwareType}|${serialNumber}|${readingName}`;

// Map a named preset to the set of matching series keys. Falls back to all
// series for Production/Export when nothing matches (mixed-brand sites), and to
// an empty selection for Inverter temperature (site has no temperature feed).
const presetSelection = (
  preset: RawPresetName,
  readings: AvailableReading[]
): string[] => {
  if (preset === "inverter-temperature") {
    return readings
      .filter(
        (r) => r.hardwareType === "inverter" && /temp/i.test(r.readingName)
      )
      .map((r) => r.key);
  }
  // production-export (and any unexpected value) → the historical default set.
  const matched = readings
    .filter((r) =>
      PRODUCTION_EXPORT_FILTER.has(`${r.hardwareType}|${r.readingName}`)
    )
    .map((r) => r.key);
  return matched.length > 0 ? matched : readings.map((r) => r.key);
};

const RAW_PRESETS: { key: RawPresetName; label: string }[] = [
  { key: "production-export", label: "Production/Export" },
  { key: "inverter-temperature", label: "Inverter temperature" },
];

interface SiteReadingsRawProps {
  site: SiteAllowStringValues;
  siteId: string;
  selectedEsysUuid: string | undefined;
  formatters: Formatters;
  initialStartDate: string;
  initialEndDate: string;
  urlParamsConsumed: boolean;
  onUrlParamsConsumed: () => void;
  onDateChange: (startDate: string, endDate: string) => void;
  rawPreset: RawPresetName;
  renderMode: RenderMode;
  onRenderModeChange: (mode: RenderMode) => void;
  outlierMode: OutlierMode;
  onOutlierModeChange: (mode: OutlierMode) => void;
  onRawPresetChange: (preset: RawPresetName) => void;
}

/** A labelled row of mutually exclusive options, styled like the presets. */
const ToggleGroup = <T extends string>({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: [T, string, string][];
  selected: T;
  onSelect: (value: T) => void;
}) => (
  <>
    <span style={{ color: "#666" }}>{label}</span>
    {options.map(([value, text, title]) => (
      <span
        key={value}
        onClick={() => onSelect(value)}
        title={title}
        style={{
          cursor: "pointer",
          fontWeight: selected === value ? 700 : 400,
          color: selected === value ? "#333" : "#666",
          textDecoration: selected === value ? "none" : "underline",
          userSelect: "none",
        }}
      >
        {text}
      </span>
    ))}
  </>
);

const SiteReadingsRaw = ({
  site,
  siteId,
  selectedEsysUuid,
  formatters,
  initialStartDate,
  initialEndDate,
  urlParamsConsumed,
  onUrlParamsConsumed,
  onDateChange,
  rawPreset,
  onRawPresetChange,
  renderMode,
  onRenderModeChange,
  outlierMode,
  onOutlierModeChange,
}: SiteReadingsRawProps) => {
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);
  const [timePeriod, setTimePeriod] = useState<string>("");
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedReadings, setSelectedReadings] = useState<string[]>([]);
  const [readingsFilterInitialized, setReadingsFilterInitialized] =
    useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const hasBattery =
    site?.totalBatterySummary !== undefined &&
    site?.totalBatterySummary !== null;

  // Fetch raw readings data - initial query to get available date range
  const rawDataRangeQuery = useChartData({
    dataType: "readings_data_raw",
    options: {},
    siteId: siteId,
    enabled: true,
  });

  // Calculate the date range to request for raw data (max 90 days)
  const rawDataOptions = useMemo(() => {
    if (!startDate || !endDate) return {};

    const start = new Date(startDate);
    const end = new Date(endDate);
    const endPlusOne = new Date(end);
    endPlusOne.setDate(endPlusOne.getDate() + 1);

    const daysDiff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );

    let effectiveStartDate: string;
    let effectiveEndDate: string;

    if (daysDiff > 90) {
      const truncatedStart = new Date(end);
      truncatedStart.setDate(truncatedStart.getDate() - 89);
      effectiveStartDate = truncatedStart.toISOString();
      effectiveEndDate = endPlusOne.toISOString();
    } else {
      effectiveStartDate = start.toISOString();
      effectiveEndDate = endPlusOne.toISOString();
    }

    const options: Record<string, unknown> = {
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
    };

    if (readingsFilterInitialized) {
      options.selectedSeries = selectedReadings;
    }

    return options;
  }, [startDate, endDate, readingsFilterInitialized, selectedReadings]);

  // Fetch raw readings data with date range and series filter
  const rawDataQuery = useChartData({
    dataType: "readings_data_raw",
    options: rawDataOptions,
    siteId: siteId,
    enabled:
      Object.keys(rawDataOptions).length > 0 && readingsFilterInitialized,
  });

  // rawDataQuery is disabled until a date range + filter are initialised (which
  // only happens once the range query returns data); use isInitialLoading so a
  // site with no raw readings doesn't hang on the spinner forever under
  // @tanstack/react-query v4 (a disabled query reports isLoading: true). The
  // range query is always enabled, so its plain isLoading is fine.
  const isLoading =
    rawDataRangeQuery.isLoading || rawDataQuery.isInitialLoading;
  const queryError = rawDataQuery.error || rawDataRangeQuery.error;
  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : "Unknown error"
    : null;

  // Calculate available time periods from the data
  const availableTimePeriods = useMemo(() => {
    const periods: string[] = [];
    periods.push("last7days");
    periods.push("all");

    let data: { x: string }[] = [];
    if (rawDataRangeQuery.data) {
      const rawData = rawDataRangeQuery.data as ReadingsDataRawResponse;
      const filteredSeries = selectedEsysUuid
        ? rawData.series.filter((s) => s.esysUuid === selectedEsysUuid)
        : rawData.series;
      const timestamps = new Set<string>();
      filteredSeries.forEach((s) => {
        s.data.x.forEach((timestamp) => {
          timestamps.add(new Date(timestamp).toISOString().split("T")[0]);
        });
      });
      data = Array.from(timestamps).map((x) => ({ x }));
    }

    if (data.length > 0) {
      const yearMonths = new Map<string, Set<string>>();

      data.forEach((point) => {
        const dateStr = formatters.toDateString(point.x);
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(0, 7);

        if (!yearMonths.has(year)) {
          yearMonths.set(year, new Set());
        }
        yearMonths.get(year)!.add(month);
      });

      const sortedYears = Array.from(yearMonths.keys()).sort().reverse();
      sortedYears.forEach((year) => {
        periods.push(year);
        const months = Array.from(yearMonths.get(year)!).sort().reverse();
        months.forEach((month) => periods.push(month));
      });

      periods.push("custom");
    }

    return periods;
  }, [rawDataRangeQuery.data, selectedEsysUuid, formatters]);

  // Extract unique source values from the data
  const availableSources = useMemo(() => {
    const sources = new Set<string>();

    if (rawDataQuery.data) {
      const rawData = rawDataQuery.data as ReadingsDataRawResponse;
      const filteredSeries = selectedEsysUuid
        ? rawData.series.filter((s) => s.esysUuid === selectedEsysUuid)
        : rawData.series;
      filteredSeries.forEach((s) => {
        s.data.source.forEach((source) => {
          if (source) sources.add(source);
        });
      });
    }

    return Array.from(sources).sort();
  }, [rawDataQuery.data, selectedEsysUuid]);

  // Extract available readings from metadata query (availableSeries)
  const availableReadings = useMemo(() => {
    if (!rawDataRangeQuery.data) return [];

    const rawData = rawDataRangeQuery.data as ReadingsDataRawResponse;
    if (!rawData.availableSeries) return [];

    const series = selectedEsysUuid
      ? rawData.availableSeries.filter((s) => s.esysUuid === selectedEsysUuid)
      : rawData.availableSeries;

    return series
      .map((s) => ({
        key: makeReadingKey(s.hardwareType, s.serialNumber, s.readingName),
        hardwareType: s.hardwareType,
        serialNumber: s.serialNumber,
        readingName: s.readingName,
      }))
      .sort((a, b) => {
        if (a.hardwareType !== b.hardwareType)
          return a.hardwareType.localeCompare(b.hardwareType);
        if (a.serialNumber !== b.serialNumber)
          return a.serialNumber.localeCompare(b.serialNumber);
        return a.readingName.localeCompare(b.readingName);
      });
  }, [rawDataRangeQuery.data, selectedEsysUuid]);

  // Initialize source filter with all sources when they become available
  useEffect(() => {
    if (availableSources.length > 0 && selectedSources.length === 0) {
      setSelectedSources(availableSources);
    }
  }, [availableSources, selectedSources.length]);

  // Derive the selection from the active named preset whenever the preset or the
  // available series change (e.g. an esys switch re-applies the preset). For
  // "custom" (the user hand-edited the selection) we keep the selection, but
  // drop any keys not present in the current available series — otherwise
  // switching esys would leave the previous esys's serial keys selected and the
  // chart would render the wrong esys's data.
  useEffect(() => {
    if (availableReadings.length === 0) return;
    if (rawPreset === "custom") {
      const availableKeys = new Set(availableReadings.map((r) => r.key));
      setSelectedReadings((prev) => {
        const pruned = prev.filter((k) => availableKeys.has(k));
        return pruned.length === prev.length ? prev : pruned;
      });
    } else {
      setSelectedReadings(presetSelection(rawPreset, availableReadings));
    }
    setReadingsFilterInitialized(true);
  }, [availableReadings, rawPreset]);

  const handleReadingsChange = (keys: string[]) => {
    setSelectedReadings(keys);
    // A manual edit is no longer a named preset.
    if (rawPreset !== "custom") onRawPresetChange("custom");
  };

  const handlePresetClick = (preset: RawPresetName) => {
    onRawPresetChange(preset);
    setSelectedReadings(presetSelection(preset, availableReadings));
  };

  // Helper function to find matching time period for given dates
  const findMatchingPeriod = (
    newStartDate: string,
    newEndDate: string,
    periods: string[]
  ): string | undefined => {
    return periods.find((period) => {
      if (
        period === "all" ||
        period === "custom" ||
        period === "last7days" ||
        period === "last30days"
      )
        return false;

      if (period.length === 4) {
        return (
          newStartDate === `${period}-01-01` && newEndDate === `${period}-12-31`
        );
      } else if (period.length === 7) {
        const [year, month] = period.split("-");
        const monthNum = parseInt(month, 10);
        const lastDay = new Date(parseInt(year), monthNum, 0).getDate();
        return (
          newStartDate === `${period}-01` &&
          newEndDate === `${period}-${lastDay.toString().padStart(2, "0")}`
        );
      }
      return false;
    });
  };

  // Initialize with default period when data is available
  useEffect(() => {
    if (!isInitialized && rawDataRangeQuery.data) {
      // If dates were provided in URL and not yet consumed, use them
      if (initialStartDate && initialEndDate && !urlParamsConsumed) {
        setStartDate(initialStartDate);
        setEndDate(initialEndDate);
        const matchingPeriod = findMatchingPeriod(
          initialStartDate,
          initialEndDate,
          availableTimePeriods
        );
        setTimePeriod(matchingPeriod || "custom");
        setIsInitialized(true);
        onUrlParamsConsumed();
        return;
      }

      setTimePeriod("last7days");

      const rawData = rawDataRangeQuery.data as ReadingsDataRawResponse;
      if (rawData.availableEnd) {
        const latestDate = new Date(rawData.availableEnd);
        const endDateObj = new Date(latestDate);
        const startDateObj = new Date(latestDate);
        startDateObj.setDate(startDateObj.getDate() - 6);

        const formatDate = (date: Date) => {
          return date.toISOString().split("T")[0];
        };

        const newStartDate = formatDate(startDateObj);
        const newEndDate = formatDate(endDateObj);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        onDateChange(newStartDate, newEndDate);
      }

      setIsInitialized(true);
    }
  }, [
    rawDataRangeQuery.data,
    isInitialized,
    initialStartDate,
    initialEndDate,
    availableTimePeriods,
    urlParamsConsumed,
    onUrlParamsConsumed,
    onDateChange,
  ]);

  // Handle time period changes
  useEffect(() => {
    if (timePeriod === "last7days") {
      if (rawDataRangeQuery.data) {
        const rawData = rawDataRangeQuery.data as ReadingsDataRawResponse;
        if (rawData.availableEnd) {
          const latestDate = new Date(rawData.availableEnd);
          const endDateObj = new Date(latestDate);
          const startDateObj = new Date(latestDate);
          startDateObj.setDate(startDateObj.getDate() - 6);

          const formatDate = (date: Date) => {
            return date.toISOString().split("T")[0];
          };

          const newStartDate = formatDate(startDateObj);
          const newEndDate = formatDate(endDateObj);
          setStartDate(newStartDate);
          setEndDate(newEndDate);
          onDateChange(newStartDate, newEndDate);
        }
      }
    } else if (timePeriod === "all") {
      if (rawDataRangeQuery.data) {
        const rawData = rawDataRangeQuery.data as ReadingsDataRawResponse;
        if (rawData.availableStart && rawData.availableEnd) {
          const start = new Date(rawData.availableStart);
          const end = new Date(rawData.availableEnd);
          const daysDiff = Math.ceil(
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
          );

          if (daysDiff > 90) {
            const truncatedStart = new Date(end);
            truncatedStart.setDate(truncatedStart.getDate() - 89);
            const newStartDate = truncatedStart.toISOString().split("T")[0];
            const newEndDate = end.toISOString().split("T")[0];
            setStartDate(newStartDate);
            setEndDate(newEndDate);
            onDateChange(newStartDate, newEndDate);
          } else {
            const newStartDate = start.toISOString().split("T")[0];
            const newEndDate = end.toISOString().split("T")[0];
            setStartDate(newStartDate);
            setEndDate(newEndDate);
            onDateChange(newStartDate, newEndDate);
          }
        }
      }
    }
  }, [timePeriod, rawDataRangeQuery.data, onDateChange]);

  // Handle time period selection
  const handleTimePeriodChange = (period: string) => {
    setTimePeriod(period);

    if (period === "last7days" || period === "all") {
      // Dates will be calculated by the useEffect
    } else if (period === "custom") {
      // Keep existing dates
    } else if (period.includes("-") && period.length === 7) {
      const [year, month] = period.split("-");
      const monthNum = parseInt(month, 10);
      const lastDay = new Date(parseInt(year), monthNum, 0).getDate();
      const newStartDate = `${period}-01`;
      const newEndDate = `${period}-${lastDay.toString().padStart(2, "0")}`;
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      onDateChange(newStartDate, newEndDate);
    } else {
      const newStartDate = `${period}-01-01`;
      const newEndDate = `${period}-12-31`;
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      onDateChange(newStartDate, newEndDate);
    }
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    onDateChange(newStartDate, newEndDate);

    const matchingPeriod = findMatchingPeriod(
      newStartDate,
      newEndDate,
      availableTimePeriods
    );
    setTimePeriod(matchingPeriod || "custom");
  };

  const hasData =
    rawDataQuery.data &&
    (rawDataQuery.data as ReadingsDataRawResponse).series.length > 0;

  return (
    <>
      <Row className="mb-3">
        <Col sm={3}>
          <Form.Group>
            <Form.Label>Time Period</Form.Label>
            <Form.Select
              value={timePeriod}
              onChange={(e) => handleTimePeriodChange(e.target.value)}
            >
              {availableTimePeriods.map((period) => {
                let label = period;
                if (period === "last7days") {
                  label = "Last 7 days";
                } else if (period === "all") {
                  label = "All available";
                } else if (period === "custom") {
                  label = "Custom";
                }
                return (
                  <option key={period} value={period}>
                    {label}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col sm={6}>
          <Form.Group>
            <Form.Label>Date Range</Form.Label>
            <DateRangePicker
              value={{
                start: startDate ? new Date(startDate) : null,
                end: endDate ? new Date(endDate) : null,
              }}
              onChange={({ start, end }) => {
                const newStart = start ? start.toISOString().split("T")[0] : "";
                const newEnd = end ? end.toISOString().split("T")[0] : "";
                handleDateChange(newStart, newEnd);
              }}
              mode="date"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        {availableReadings.length > 0 && (
          <Col sm={5} className="mb-3">
            <Form.Group>
              <Form.Label>Select readings</Form.Label>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginBottom: "6px",
                  fontSize: "0.8rem",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ color: "#666" }}>Presets:</span>
                {RAW_PRESETS.map(({ key, label }) => (
                  <span
                    key={key}
                    onClick={() => handlePresetClick(key)}
                    style={{
                      cursor: "pointer",
                      fontWeight: rawPreset === key ? 700 : 400,
                      color: rawPreset === key ? "#333" : "#666",
                      textDecoration: rawPreset === key ? "none" : "underline",
                      userSelect: "none",
                    }}
                  >
                    {label}
                  </span>
                ))}
                {rawPreset === "custom" && (
                  <span style={{ color: "#999" }}>(custom)</span>
                )}
              </div>
              <ReadingsSeriesPicker
                options={availableReadings.map((r) => ({
                  key: r.key,
                  label: `(${r.hardwareType}, ${r.serialNumber}, ${r.readingName})`,
                }))}
                selected={selectedReadings}
                onChange={handleReadingsChange}
              />
              {/* How the selected series are drawn, so it sits under the series
                  rather than beside the presets that choose them. */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginTop: "6px",
                  fontSize: "0.8rem",
                  flexWrap: "wrap",
                }}
              >
                {/* Counter feeds (Solis, Reduxi) report cumulative registers,
                    which draw as a monotonic ramp: everything the operator wants
                    to know is in the gradient. Delta plots the gradient. */}
                <ToggleGroup
                  label="Rendering mode:"
                  options={[
                    ["value", "Value", "The reading itself"],
                    [
                      "delta",
                      "Delta",
                      "Each point as the change since the previous reading - for cumulative counters",
                    ],
                  ]}
                  selected={renderMode}
                  onSelect={onRenderModeChange}
                />
                {/* A daily counter rollover is one enormous negative delta that
                    sets the y-axis scale and flattens the real data. Shown by
                    default - hiding discards readings, so the operator asks. */}
                <ToggleGroup
                  label="Outliers:"
                  options={[
                    ["show", "Shown", "Every reading, rollovers included"],
                    [
                      "hide",
                      "Hidden",
                      "Drop isolated extreme points - a counter rollover, not a sustained event",
                    ],
                  ]}
                  selected={outlierMode}
                  onSelect={onOutlierModeChange}
                />
              </div>
            </Form.Group>
          </Col>
        )}

        {availableSources.length > 0 && (
          <Col sm={3} className="mb-3">
            <Form.Group>
              <Form.Label>Select sources</Form.Label>
              <Select
                placeholder="Filter by source..."
                value={availableSources
                  .filter((s) => selectedSources.includes(s))
                  .map((s) => ({
                    label: s,
                    value: s,
                  }))}
                onChange={(values) => {
                  setSelectedSources(
                    values ? values.map((value) => value.value) : []
                  );
                }}
                options={availableSources.map((source) => ({
                  label: source,
                  value: source,
                }))}
                components={{
                  MultiValue: ({ index }) => {
                    if (index > 0) return null;
                    return (
                      <div
                        style={{
                          marginLeft: "4px",
                          color: "var(--bs-body-color)",
                        }}
                      >
                        {selectedSources.length}/{availableSources.length}{" "}
                        sources selected
                      </div>
                    );
                  },
                }}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border:
                      "var(--bs-border-width) solid var(--bs-border-color)",
                    borderRadius: "var(--bs-border-radius)",
                    color: "var(--bs-body-color)",
                    minWidth: "250px",
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    "::before": {
                      content: state.isSelected ? '"☑"' : '"☐"',
                      fontSize: "1.2em",
                    },
                    backgroundColor: state.isFocused
                      ? "var(--bs-primary-bg-subtle)"
                      : "transparent",
                    color: "var(--bs-body-color)",
                  }),
                }}
                isMulti
                isClearable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
              />
            </Form.Group>
          </Col>
        )}
      </Row>

      {isLoading && <Loading label="Loading readings data..." height="60vh" />}

      {error && (
        <Row>
          <Col>
            <div className="alert alert-danger" role="alert">
              Error loading readings data: {error}
            </div>
          </Col>
        </Row>
      )}

      {!isLoading && !error && !hasData && (
        <Row>
          <Col>
            <div className="alert alert-info" role="alert">
              No readings data available for this site
            </div>
          </Col>
        </Row>
      )}

      {!isLoading &&
        !error &&
        hasData &&
        isInitialized &&
        readingsFilterInitialized && (
          <ChartRaw
            data={rawDataQuery.data as ReadingsDataRawResponse}
            selectedEsysUuid={selectedEsysUuid}
            startDate={startDate}
            endDate={endDate}
            selectedSources={selectedSources}
            selectedReadings={selectedReadings}
            renderMode={renderMode}
            outlierMode={outlierMode}
            formatters={formatters}
          />
        )}

      {!isLoading && (
        <SourceTypeCounts
          dataMode="raw"
          processedData={undefined}
          rawData={rawDataQuery.data as ReadingsDataRawResponse}
          hasBattery={hasBattery}
          selectedEsysUuid={selectedEsysUuid}
          startDate={startDate}
          endDate={endDate}
          formatters={formatters}
        />
      )}

      {!isLoading && rawDataQuery.data && (
        <UpdatedReadingsRaw
          data={rawDataQuery.data as ReadingsDataRawResponse}
          startDate={startDate}
          endDate={endDate}
          formatters={formatters}
        />
      )}

      {!isLoading && rawDataQuery.data && (
        <RecentDataTableRaw
          data={rawDataQuery.data as ReadingsDataRawResponse}
          selectedEsysUuid={selectedEsysUuid}
          startDate={startDate}
          endDate={endDate}
          formatters={formatters}
        />
      )}
    </>
  );
};

export default SiteReadingsRaw;
